// Shared bits of the orders page (spec/dashboard.md section 11.5; spec/api-conventions.md sections 19.3, 19.5): the
// filters, the status words and tones, the support actions and the states they start from, the money and gold texts.
import { ApiError } from '../../api/client';
import type { PaymentOrder } from '../../api/types';
import type { Tone } from '../../ui/Chip';
import { moneyText } from '../../content/liveops/stats';

export type Order = PaymentOrder;

export interface OrderListPage {
  items: Order[];
  nextCursor: string | null;
  total: number | string | null;
}

/** The URL search params of the filter row, in the order the row shows them; each one is sent as the same query param. */
export const FILTER_KEYS = ['paymentId', 'player', 'externalUserId', 'sku', 'status', 'from', 'to'] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];
export type Filters = Partial<Record<FilterKey, string>>;

export const PAGE_SIZE = 50;

export const STATUSES = ['created', 'committed', 'claimed', 'failed', 'expired'] as const;

export const STATUS_TONE: Record<string, Tone> = {
  created: 'neutral',
  committed: 'info',
  claimed: 'success',
  failed: 'danger',
  expired: 'warning',
};

export type OrderAction = 'commit' | 'claim' | 'fail';

/** The support actions (19.5): the word on the button, the states each one starts from, the state it leads to. */
export const ACTIONS: { action: OrderAction; label: string; from: readonly string[]; to: string }[] = [
  { action: 'commit', label: 'Commit', from: ['created', 'expired'], to: 'committed' },
  { action: 'claim', label: 'Claim', from: ['committed'], to: 'claimed' },
  { action: 'fail', label: 'Fail', from: ['created', 'committed', 'expired'], to: 'failed' },
];

/** The non-empty filters of the URL. */
export function readFilters(params: URLSearchParams): Filters {
  const filters: Filters = {};
  for (const k of FILTER_KEYS) {
    const v = params.get(k)?.trim();
    if (v) filters[k] = v;
  }
  return filters;
}

/** A stable text of the filters for the cache key (every change of a filter is a new list). */
export function filtersKey(filters: Filters): string {
  return FILTER_KEYS.filter((k) => filters[k] !== undefined)
    .map((k) => `${k}=${encodeURIComponent(filters[k]!)}`)
    .join('&');
}

/** An ISO instant of the URL as unix seconds for `DateTimeInput`; null when absent or unreadable. */
export function isoToSeconds(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

export function secondsToIso(seconds: number | null): string | null {
  return seconds === null ? null : new Date(seconds * 1000).toISOString();
}

export function priceText(order: Pick<Order, 'price'>): string {
  return order.price ? moneyText(Number(order.price.amount), order.price.currency) : '—';
}

export function goldText(order: Pick<Order, 'gold'>): string {
  return `${String(order.gold)} NG`;
}

export function plural(n: number | string, word: string): string {
  return `${n} ${word}${Number(n) === 1 ? '' : 's'}`;
}

export function asApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, 'unavailable', e instanceof Error ? e.message : String(e), '');
}
